import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useConsent, saveConsent } from '@/lib/consent';

const Register = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [recaptchaConfig, setRecaptchaConfig] = useState(null); // Start as null to detect loading
  const [recaptchaKey, setRecaptchaKey] = useState(0); // Key to force remount
  const [recaptchaReloading, setRecaptchaReloading] = useState(false); // Loading state for recaptcha
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'unverified' or 'verified'
  const [existingEmail, setExistingEmail] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const { consent } = useConsent();
  const recaptchaAllowed = !!(consent && consent.recaptcha);

  // Check password strength
  useEffect(() => {
    const pwd = formData.password;
    setPasswordStrength({
      length: pwd.length >= 12,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*]/.test(pwd)
    });
  }, [formData.password]);

  // Fetch reCAPTCHA configuration from backend
  useEffect(() => {
    const fetchRecaptchaConfig = async () => {
      try {
        const response = await axios.get(`${API}/settings/recaptcha/public`);
        setRecaptchaConfig(response.data);
      } catch (error) {
        console.error('Error fetching reCAPTCHA config:', error);
        // Fallback to .env if API call fails
        setRecaptchaConfig({
          site_key: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
          enabled: true
        });
      }
    };
    fetchRecaptchaConfig();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error(t('register.passwordMismatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('register.passwordTooShort'));
      return;
    }

    // Get reCAPTCHA token only if reCAPTCHA is enabled
    let recaptchaToken = 'disabled';
    if (recaptchaConfig && recaptchaConfig.enabled && recaptchaConfig.site_key) {
      recaptchaToken = recaptchaRef.current?.getValue();
      if (!recaptchaToken) {
        toast.error(t('register.recaptchaRequired'));
        return;
      }
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/register`, {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        recaptcha_token: recaptchaToken
      });
      toast.success(t('register.registerSuccess'));
      navigate('/bess/login');
    } catch (error) {
      // Check for special user exists responses
      if (error.response?.status === 409 && typeof error.response?.data?.detail === 'object') {
        const detail = error.response.data.detail;
        setExistingEmail(detail.email);
        setModalType(detail.type);
        setShowModal(true);
      } else {
        // Handle validation errors (array of error objects)
        let errorMessage = t('register.registerFailedGeneric');
        
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          
          // If detail is an array (Pydantic validation errors)
          if (Array.isArray(detail)) {
            errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } 
          // If detail is a string
          else if (typeof detail === 'string') {
            errorMessage = detail;
          }
          // If detail is an object with a message property
          else if (typeof detail === 'object' && detail.message) {
            errorMessage = detail.message;
          }
        }
        
        toast.error(errorMessage);
      }
      
      // Force remount reCAPTCHA widget with delay (Google needs ~5s to cleanup)
      if (recaptchaConfig && recaptchaConfig.enabled && recaptchaConfig.site_key) {
        setRecaptchaReloading(true);
        setTimeout(() => {
          setRecaptchaKey(prev => prev + 1);
          setRecaptchaReloading(false);
        }, 5000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/auth/resend-verification`, { email: existingEmail });
      toast.success(t('register.verificationResent'));
      setShowModal(false);
      navigate('/bess/login');
    } catch (error) {
      // Handle validation errors
      let errorMessage = t('register.resendError');
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object with a message property
        else if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPasswordReset = async () => {
    try {
      setLoading(true);
      await axios.post(`${API}/auth/request-password-reset`, { email: existingEmail });
      toast.success(t('register.resetEmailSent'));
      setShowModal(false);
      navigate('/bess/login');
    } catch (error) {
      // Handle rate limiting error
      if (error.response?.status === 429 && typeof error.response?.data?.detail === 'object') {
        const detail = error.response.data.detail;
        toast.error(detail.message || t('register.resetRateLimited'));
      } else {
        // Handle validation errors (array of error objects)
        let errorMessage = t('register.resendError');
        
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          
          // If detail is an array (Pydantic validation errors)
          if (Array.isArray(detail)) {
            errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } 
          // If detail is a string
          else if (typeof detail === 'string') {
            errorMessage = detail;
          }
          // If detail is an object with a message property
          else if (typeof detail === 'object' && detail.message) {
            errorMessage = detail.message;
          }
        }
        
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" data-testid="register-page">
      <div className="max-w-md w-full">
        <div className="card animate-fadeIn">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">{t('register.title')}</h2>
            <p className="text-slate-600">{t('register.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username">{t('register.username')}</Label>
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                required
                placeholder="IhrBenutzername"
                data-testid="username-input"
              />
            </div>

            <div>
              <Label htmlFor="email">{t('register.email')}</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="ihre@email.de"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('register.password')}</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder={t('register.passwordPlaceholder')}
                data-testid="password-input"
              />
              <div className="mt-2 p-3 bg-slate-50 rounded border border-slate-200">
                <p className="text-xs font-semibold text-slate-700 mb-2">{t('register.passwordRequirementsTitle')}</p>
                <div className="space-y-1">
                  <div className={`text-xs flex items-center gap-2 ${passwordStrength.length ? 'text-green-600' : 'text-slate-500'}`}>
                    <span>{passwordStrength.length ? '✓' : '○'}</span>
                    <span>{t('register.reqLength')}</span>
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${passwordStrength.uppercase ? 'text-green-600' : 'text-slate-500'}`}>
                    <span>{passwordStrength.uppercase ? '✓' : '○'}</span>
                    <span>{t('register.reqUppercase')}</span>
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${passwordStrength.lowercase ? 'text-green-600' : 'text-slate-500'}`}>
                    <span>{passwordStrength.lowercase ? '✓' : '○'}</span>
                    <span>{t('register.reqLowercase')}</span>
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${passwordStrength.number ? 'text-green-600' : 'text-slate-500'}`}>
                    <span>{passwordStrength.number ? '✓' : '○'}</span>
                    <span>{t('register.reqNumber')}</span>
                  </div>
                  <div className={`text-xs flex items-center gap-2 ${passwordStrength.special ? 'text-green-600' : 'text-slate-500'}`}>
                    <span>{passwordStrength.special ? '✓' : '○'}</span>
                    <span>{t('register.reqSpecial')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">{t('register.confirmPassword')}</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder={t('register.confirmPasswordPlaceholder')}
                data-testid="confirm-password-input"
              />
            </div>

            <div>
              <Label htmlFor="phone">{t('register.phone')}</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+49 123 456789"
                data-testid="phone-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t('register.phoneHint')}
              </p>
            </div>

            {recaptchaConfig === null ? (
              <div className="flex justify-center py-4">
                <div className="flex items-center gap-3 py-4 px-6 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                  <span className="text-sm text-gray-800 font-medium">
                    {t('register.recaptchaLoading')}
                  </span>
                </div>
              </div>
            ) : recaptchaConfig.enabled && recaptchaConfig.site_key && !recaptchaAllowed ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900" data-testid="recaptcha-consent">
                <p className="mb-3">{t('consent.recaptchaNeeded')}</p>
                <button
                  type="button"
                  onClick={() => saveConsent({ recaptcha: true })}
                  className="px-4 py-2 rounded-md border border-amber-400 bg-white text-amber-900 font-medium hover:bg-amber-100"
                  data-testid="recaptcha-consent-accept"
                >
                  {t('consent.recaptchaLoad')}
                </button>
                <p className="mt-3 text-xs">
                  {t('consent.recaptchaAlternative')}{' '}
                  <Link to="/bess/datenschutz" className="underline">{t('consent.moreInfo')}</Link>
                </p>
              </div>
            ) : recaptchaConfig.enabled && recaptchaConfig.site_key ? (
              <div className="flex justify-center">
                {recaptchaReloading ? (
                  <div className="flex items-center gap-3 py-4 px-6 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-blue-800 font-medium">
                      {t('register.recaptchaReloading')}
                    </span>
                  </div>
                ) : (
                  <ReCAPTCHA
                    key={recaptchaKey}
                    ref={recaptchaRef}
                    sitekey={recaptchaConfig.site_key}
                    data-testid="recaptcha"
                  />
                )}
              </div>
            ) : null}

            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="text-xs text-slate-600">
                {t('register.confirmationNotice')}
              </p>
              <p className="text-xs text-slate-600 mt-2">
                <Link to="/bess/datenschutz" className="underline" data-testid="register-privacy-link">
                  {t('consent.moreInfo')}
                </Link>
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? t('register.submitLoading') : t('register.submit')}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-slate-600">{t('register.haveAccount')} </span>
            <Link to="/bess/login" className="text-green-600 hover:text-green-700 font-semibold" data-testid="login-link">
              {t('register.loginNow')}
            </Link>
          </div>
        </div>

        {/* Modal for existing user */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
              {modalType === 'user_unverified' ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t('register.unverifiedTitle')}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">
                    {t('register.unverifiedText')}
                  </p>
                  <p className="text-slate-700 font-medium mb-6">
                    {t('register.unverifiedQuestion')}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowModal(false)}
                      variant="outline"
                      className="flex-1"
                      disabled={loading}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleResendVerification}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? t('register.sending') : t('register.sendLink')}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">{t('register.existingTitle')}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 mb-6">
                    {t('register.existingText')}
                  </p>
                  <p className="text-slate-700 font-medium mb-6">
                    {t('register.existingQuestion')}
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => setShowModal(false)}
                      variant="outline"
                      className="flex-1"
                      disabled={loading}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button
                      onClick={handleRequestPasswordReset}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? t('register.sending') : t('register.resetPassword')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;
